import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faBolt, faReply, faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { ReachOutDescriptor } from "./leadPanelActions.utils";

interface ReachOutActionButtonProps {
	descriptor: ReachOutDescriptor;
	firstName: string;
	disabled?: boolean;
	disabledHint?: string;
	/**
	 * The lead is being added to the Compose list before the Chat view can open (PREC-3825) — the
	 * add runs the enrichment pipeline server-side and takes seconds, so the button shows progress.
	 */
	loading?: boolean;
	onClick: () => void;
}

const ICON_BY_KIND: Record<ReachOutDescriptor["kind"], IconDefinition> = {
	"reach-out": faBolt,
	"review-drafts": faSparkles,
	reply: faReply,
};

/**
 * The primary indigo button in the lead-panel action row. Its label/icon follow the reach-out state
 * machine: "Reply to {name}" when a reply is awaited, "Review drafts (n)" when AI drafts are ready,
 * else "Reach out to {name}". Clicking opens the panel's Chat view. Disabled (with a hint) when the
 * lead is on no list, since the Chat view needs a `ListPerson`.
 */
export const ReachOutActionButton: FunctionComponent<ReachOutActionButtonProps> = ({
	descriptor,
	firstName,
	disabled,
	disabledHint,
	loading = false,
	onClick,
}) => {
	const { t } = useTranslation();

	// A count of 0 means the sequence shape wasn't recognised — show the count-less
	// label rather than a nonsensical "Review drafts (0)".
	const label = loading
		? t("contacts.leadPanel.actions.reachOut.preparing")
		: descriptor.kind === "reply"
			? t("contacts.leadPanel.actions.reachOut.reply", { name: firstName })
			: descriptor.kind === "review-drafts"
				? descriptor.draftCount > 0
					? t("contacts.leadPanel.actions.reachOut.reviewDrafts", { count: descriptor.draftCount })
					: t("contacts.leadPanel.actions.reachOut.reviewDraftsNoCount")
				: t("contacts.leadPanel.actions.reachOut.default", { name: firstName });

	return (
		<button
			className={classNames(
				"flex h-8 flex-[2] cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border border-transparent bg-blue-action px-2.5 text-xs font-semibold text-white duration-200",
				"hover:bg-blue-action-dark hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-action disabled:hover:shadow-none"
			)}
			data-testid="lead-panel-reach-out"
			disabled={disabled || loading}
			title={disabled ? disabledHint : undefined}
			type="button"
			onClick={onClick}
		>
			{loading ? (
				<span
					className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent"
					data-testid="lead-panel-reach-out-spinner"
				/>
			) : (
				<Icon className="w-3.5" icon={ICON_BY_KIND[descriptor.kind]} />
			)}
			<span className="truncate">{label}</span>
		</button>
	);
};
